import React, { memo, useContext, useState } from 'react'
import PropTypes from 'prop-types'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'

import PURCHASE_TYPES from '../../constants/purchaseTypes'
import trackingEvents from '../../constants/trackingEvents'
import ConfigContext from '../../contexts/configContext'
import { formatCurrency } from '../../helpers/currencyHelpers'
import { translate } from '../../helpers/stringHelpers'

import Modal from '../modal'
import ModalBody from '../modalBody'
import Button from '../button'
import FeesAlert from '../feesAlert'
import { trackEvent } from '../../withTracking'

import { listings, listingSales } from '../../actions'

const {
  CATEGORY_LISTING,
  ACTION_BUY
} = trackingEvents
const PurchaseConfirmationModalBuy = ({
  isOpen,
  listingUuid,
  onClose
}) => {
  const { locale } = useContext(ConfigContext)
  const [isAwaitingResponse, setAwaitingResponse] = useState(false)

  const dispatch = useDispatch()
  const listing = useSelector(state => state.lots.listings[listingUuid], shallowEqual)
  const sale = useSelector(state => listingUuid && state.listingSales.sales[listingUuid], shallowEqual)
  const userUuid = useSelector(state => state.users.user.uuid)
  const userId = useSelector(state => state.users.user.id)
  const auctionUuid = listing?.isMarketplace ? 'marketplace' : listing?.auctionUuid
  const isFavourited = useSelector(state => state.lots.favourites[auctionUuid]?.[listingUuid])
  const registrantId = useSelector(state => (userUuid && state.registrations.registrantIdsByUserUuidByAuction[listing?.auctionUuid]?.[userUuid]?.id))
  const purchaseTypeId = useSelector(state => state.listingSales.sales[listingUuid]?.purchaseTypeId)

  if (purchaseTypeId !== PURCHASE_TYPES.BUY_NOW || !listing) return null
  const { currencyCode, buyNowValue } = sale
  const handleBuyConfirm = async (e) => {
    e.preventDefault()
    try {
      setAwaitingResponse(true)
      await dispatch(listingSales.placeBuy(listingUuid, listing.id, registrantId, userUuid, userId, buyNowValue))
      trackEvent(CATEGORY_LISTING, ACTION_BUY, listingUuid, buyNowValue)
      if (!isFavourited) {
        dispatch(listings.favouriteToggle(auctionUuid, listingUuid, listing.id, userId))
      }
    } catch (err) {
      console.error(err)
      toast.error(err)
    }
    onClose()
    setAwaitingResponse()
  }

  return (
    <Modal onClose={onClose} isOpen={isOpen} id='buyModal'>
      <ModalBody>
        <h4 className='modal__primary-title'>{translate('JspPublicCodes_JSP_BIDDING_BUY_NOW')}</h4>
        <h5>{listing.title}</h5>
        <form
          onSubmit={handleBuyConfirm}
        >
          <p><strong>{translate('JspPublicCodes_JSP_LISTING_BUY_CONFIRMATION')}</strong></p>
          <p dangerouslySetInnerHTML={{ __html: translate('PageCodes_PAGE_BUY_NOW_HELP') }} />
          <FeesAlert listingUuid={listingUuid} />
          <Button
            id='buy__cancel'
            onClick={onClose}
          >
            {translate('JSTextCodes_CANCEL')}
          </Button>
          <Button
            disabled={isAwaitingResponse}
            id='buy__submit'
            type='submit'
            color='primary'
            className='pull-right'
          >
            {translate('JspPublicCodes_JSP_BIDDING_BUY_NOW')} - {formatCurrency(locale, currencyCode, buyNowValue)}
          </Button>
        </form>
      </ModalBody>
    </Modal>
  )
}

PurchaseConfirmationModalBuy.propTypes = {
  isOpen: PropTypes.bool,
  listingUuid: PropTypes.string,
  onClose: PropTypes.func.isRequired
}

const isEqual = (a, b) => a.isOpen === b.isOpen &&
  a.listingUuid === b.listingUuid

export default memo(PurchaseConfirmationModalBuy, isEqual)
